import { aggregateInsights } from '../../services/meta-api.js';

const number = (value) => Number(value || 0);
const valid = (value) => Number.isFinite(number(value));

/** Derived numbers only: no global benchmarks or invented attribution. */
export function summarizeAdInsights(rows, filters = {}) {
  const map = new Map();
  for (const row of rows || []) {
    const id = String(row?.adId || '');
    if (!id) continue;
    if (filters.campaign && filters.campaign !== 'all' &&
        String(row.campaignId) !== String(filters.campaign)) continue;
    if (filters.adset && filters.adset !== 'all' &&
        String(row.adsetId) !== String(filters.adset)) continue;
    if (filters.ad && filters.ad !== 'all' &&
        String(row.adId) !== String(filters.ad)) continue;
    const group = map.get(id) || { id, name: row.adName || id, campaignId: row.campaignId,
      campaignName: row.campaignName || row.campaignId || '', adsetId: row.adsetId,
      adsetName: row.adsetName || '', items: [] };
    group.items.push(row);
    map.set(id, group);
  }
  return [...map.values()].map((group) => ({
    ...group,
    metrics: aggregateInsights(group.items),
    linkClicks: group.items.every((item) => item.inlineLinkClicks != null)
      ? group.items.reduce((sum, item) => sum + number(item.inlineLinkClicks), 0)
      : null
  })).sort((a, b) => b.metrics.spend - a.metrics.spend);
}

export function linkClickMetrics(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length || list.some((r) => r.inlineLinkClicks == null || !valid(r.inlineLinkClicks))) return null;
  const spend = list.reduce((sum, r) => sum + number(r.spend), 0);
  const impressions = list.reduce((sum, r) => sum + number(r.impressions), 0);
  const links = list.reduce((sum, r) => sum + number(r.inlineLinkClicks), 0);
  return {
    clicks: links,
    ctr: impressions ? (links / impressions) * 100 : null,
    cpc: links ? spend / links : null
  };
}

/**
 * Relative, internal signals. Every recommendation has a minimum evidence
 * gate. A signal is NOT an automatic Meta Ads management action.
 */
export function buildMarketingSignals(performance) {
  const rows = (performance || []).filter((r) => r.metrics && number(r.metrics.spend) > 0);
  const baselineSpend = rows.reduce((sum, r) => sum + number(r.metrics.spend), 0);
  const baselineLeads = rows.reduce((sum, r) => sum + number(r.metrics.leads), 0);
  const weightedCpl = baselineLeads >= 10 ? baselineSpend / baselineLeads : null;
  const signals = [];
  for (const row of rows) {
    const m = row.metrics;
    const impressions = number(m.impressions);
    const leads = number(m.leads);
    const spend = number(m.spend);
    if (weightedCpl && leads >= 5 && impressions >= 1000 && m.cpl > weightedCpl * 1.35) {
      signals.push({
        level: 'hipótesis', campaignId: row.id, campaign: row.name,
        title: 'CPL por encima de la media de la cuenta',
        evidence: 'CPL ' + m.cpl.toFixed(0) + ' vs. media ponderada interna ' + weightedCpl.toFixed(0) + '.',
        action: 'Comparar objetivo, público y creativo con los resultados de Meta; probar una variante, no pausar automáticamente.'
      });
    } else if (weightedCpl && leads === 0 && impressions >= 1500 && spend >= weightedCpl * 3) {
      signals.push({
        level: 'revisar', campaignId: row.id, campaign: row.name,
        title: 'Inversión sin leads atribuidos',
        evidence: 'Gasto ' + spend.toFixed(0) + ' con ' + impressions + ' impresiones y 0 leads Meta reportados.',
        action: 'Revisar evento de conversión, formulario, destino y atribución antes de aumentar presupuesto.'
      });
    }
  }
  if (!signals.length) {
    signals.push({
      level: 'información', campaignId: '', campaign: 'Vista general',
      title: 'Aún no hay señales con suficiente evidencia',
      evidence: 'Se aplican umbrales mínimos para evitar recomendaciones con pocas observaciones.',
      action: 'Revisar campañas y anuncios de Meta en una ventana mayor.'
    });
  }
  return signals.sort((a, b) => {
    const priority = (item) => item.level === 'revisar' ? 0 : item.level === 'hipótesis' ? 1 : 2;
    return priority(a) - priority(b);
  }).slice(0, 9);
}

export function makeTrendPoints(rows, metric, width = 720, height = 155) {
  const values = (rows || []).map((r) => number(metric(r)));
  const top = Math.max(1, ...values);
  const bottom = 12;
  return values.map((value, i) => ({
    x: values.length > 1 ? (i / (values.length - 1)) * width : width / 2,
    y: bottom + (1 - value / top) * (height - bottom),
    value
  }));
}
